import React from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { Nav } from "@/components/Nav";
import { Hero } from "@/components/Hero";
import { Services } from "@/components/Services";
import { WhyChoose } from "@/components/WhyChoose";
import { Areas } from "@/components/Areas";
import { Reviews } from "@/components/Reviews";
import { FAQ } from "@/components/FAQ";
import { Contact } from "@/components/Contact";
import { Footer } from "@/components/Footer";
import { StickyCTA } from "@/components/StickyCTA";
import { Seo } from "@/components/Seo";
import { AuthProvider } from "@/context/AuthContext";
import { AuthCallback } from "@/pages/AuthCallback";
import { Login } from "@/pages/Login";
import { Dashboard } from "@/pages/Dashboard";
import { AdminDashboard } from "@/pages/AdminDashboard";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ServicePage } from "@/pages/ServicePage";
import { AreaPage } from "@/pages/AreaPage";
import { ServiceAreaPage } from "@/pages/ServiceAreaPage";
import { ServicesIndex, AreasIndex } from "@/pages/IndexPages";
import { BlogIndex, BlogPost } from "@/pages/Blog";
import { AdminQuotes, AdminQuoteDetail } from "@/pages/AdminQuotes";
import { AdminCustomers, AdminInvoices } from "@/pages/AdminCustomers";
import { PublicQuote } from "@/pages/PublicQuote";

const Landing = () => (
  <main data-testid="landing-page" className="bg-white text-[#0A0A0A] min-h-screen">
    <Seo
      title="BoxB Solutions | House & Office Removals Cardiff & South Wales"
      description="Professional house removals, office moves, packing and end of tenancy cleaning across Cardiff, Newport, Barry, Penarth and South Wales. Fully insured. Free quotes on WhatsApp."
      canonical="/"
    />
    <Nav />
    <Hero />
    <Services />
    <WhyChoose />
    <Areas />
    <Reviews />
    <FAQ />
    <Contact />
    <Footer />
    <StickyCTA />
  </main>
);

// Detect Emergent Auth callback (URL fragment contains session_id) BEFORE routing.
// This synchronous check prevents race conditions with route matching.
const AppRouter = () => {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/dashboard" element={
        <ProtectedRoute><Dashboard /></ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>
      } />
      <Route path="/boxbquote" element={
        <ProtectedRoute adminOnly><AdminQuotes /></ProtectedRoute>
      } />
      <Route path="/boxbquote/enquiries" element={
        <ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>
      } />
      <Route path="/boxbquote/customers" element={
        <ProtectedRoute adminOnly><AdminCustomers /></ProtectedRoute>
      } />
      <Route path="/boxbquote/invoices" element={
        <ProtectedRoute adminOnly><AdminInvoices /></ProtectedRoute>
      } />
      <Route path="/boxbquote/quotes/:id" element={
        <ProtectedRoute adminOnly><AdminQuoteDetail /></ProtectedRoute>
      } />
      <Route path="/quote/:token" element={<PublicQuote />} />

      {/* SEO pages */}
      <Route path="/services" element={<ServicesIndex />} />
      <Route path="/services/:serviceSlug" element={<ServicePage />} />
      <Route path="/services/:serviceSlug/:areaSlug" element={<ServiceAreaPage />} />
      <Route path="/areas" element={<AreasIndex />} />
      <Route path="/areas/:areaSlug" element={<AreaPage />} />
      <Route path="/blog" element={<BlogIndex />} />
      <Route path="/blog/:slug" element={<BlogPost />} />

      <Route path="*" element={<Landing />} />
    </Routes>
  );
};

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-center" richColors closeButton />
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}
